import i18n from 'i18n';
import { DeliveryTargetStatus } from 'core/rtbCampaign/RtbCampaign';

/**
 * 戶外投遞單位。門市通路鎖到門市，機台通路鎖到單一機台特性。
 */
export enum DeliveryTargetUnit {
  STORE = 'STORE',
  DEVICE = 'DEVICE'
}

/**
 * 後端在 limitation type 上區分投遞單位：門市通路存 `storeNum`、機台通路存
 * `deviceCharacteristic`。這裡以 type 判斷而不是通路設定的 deliveryUnit ——
 * deliveryUnit 只在活動列有投遞數字時才傳得到，type 則永遠跟著 limitation 本身。
 */
export const DELIVERY_TARGET_LIMITATION_UNITS: {
  [limitationType: string]: DeliveryTargetUnit;
} = {
  storeNum: DeliveryTargetUnit.STORE,
  deviceCharacteristic: DeliveryTargetUnit.DEVICE
};

/**
 * 每個投遞單位的 limitation type 對應到「縣市層節點 value → 該縣市底下的門市
 * （機台）選項」。選整個縣市時活動只存得下縣市那一個 value，底下有哪些門市
 * 只能靠這棵樹回查。列表頁自己不打這支 API，這份資料是
 * L1ObjectChannelDetailModel 取回活動時順手從既有的 attribute bag 取下來的。
 */
export type DeliveryTargetParentOptions = {
  [limitationType: string]: Map<string, SelectOptions[]>;
};

export type ParsedDeliveryTarget = {
  municipality: string;
  storeNum: string;
  storeName: string;
  characteristic?: string;
  /** 原始 option 的 value。同一個 value 可能出現多次，不當成唯一鍵。 */
  value: string;
};

export type DeliveryTargetGroup = {
  key: string;
  label: string;
  count: number;
  targets: ParsedDeliveryTarget[];
};

const UNKNOWN_MUNICIPALITY_KEY = '__unknown__';

/** 未知縣市那一列不是縣市，畫面上凡是「對縣市做的事」都要先排除它。 */
export function isUnknownMunicipalityGroup(
  group: DeliveryTargetGroup
): boolean {
  return group.key === `city:${UNKNOWN_MUNICIPALITY_KEY}`;
}

/**
 * 拆 value 用 indexOf 而不是 split：機台特性是最後一段且內容不受限，含 `/` 時
 * split 會把它切成多段。前兩個分隔符定位縣市與店號，其餘全部算機台特性。
 */
function parseValue(
  value: string,
  unit: DeliveryTargetUnit
):
  | { municipality: string; storeNum: string; characteristic?: string }
  | undefined {
  const firstSlash = value.indexOf('/');
  if (firstSlash <= 0) {
    return undefined;
  }
  const municipality = value.slice(0, firstSlash);

  if (unit === DeliveryTargetUnit.STORE) {
    const storeNum = value.slice(firstSlash + 1);
    return storeNum ? { municipality, storeNum } : undefined;
  }

  const secondSlash = value.indexOf('/', firstSlash + 1);
  if (secondSlash < 0) {
    return undefined;
  }
  const storeNum = value.slice(firstSlash + 1, secondSlash);
  const characteristic = value.slice(secondSlash + 1);
  return storeNum && characteristic
    ? { municipality, storeNum, characteristic }
    : undefined;
}

/**
 * 店名夾在 label 的中間：`縣市_店號_店名` 或 `縣市_店號_店名_機台特性`。
 * 店名與機台特性本身都可能含底線，所以錨點只取 value 解出來的欄位，不能對 label 做 split。
 *
 * 縣市那一段對不上是正常的：門市選項的 label 會依語系翻譯縣市，而 value 的縣市是原始值
 * （value 要能原樣存回 limitation）。所以縣市對不上時改用 `_店號_` 定位 —— 取第一次出現，
 * 那才是縣市與店名的邊界，之後全部算店名。連店號都找不到才整段照用，寧可顯示得長也不要顯示錯。
 */
function extractName(
  label: string,
  {
    municipality,
    storeNum,
    characteristic
  }: { municipality: string; storeNum: string; characteristic?: string }
): string {
  const prefix = `${municipality}_${storeNum}_`;
  let name: string;
  if (label.startsWith(prefix)) {
    name = label.slice(prefix.length);
  } else {
    const anchor = `_${storeNum}_`;
    const at = label.indexOf(anchor);
    if (at < 0) {
      return label;
    }
    name = label.slice(at + anchor.length);
  }

  if (characteristic !== undefined) {
    const suffix = `_${characteristic}`;
    if (!name.endsWith(suffix)) {
      return label;
    }
    name = name.slice(0, name.length - suffix.length);
  }

  return name === '' ? label : name;
}

/**
 * 選項樹查不到這間門市時，改用狀態帶回來的名字。
 *
 * 門市的版位全部停用就會從選項樹消失，`overlayLabels` 那時會把 label 換成 value
 * 字串，於是店名欄位會變成 `臺北市/118420` 這種原始值、與旁邊的店號重複。
 * 兩者同格式，所以照原本那條去頭去尾的規則解就好。
 */
export function resolveStoreName(
  target: ParsedDeliveryTarget,
  deliveryTargetName: string | null | undefined
): string {
  const lostLabel = target.storeName === target.value;
  if (!lostLabel || !deliveryTargetName) {
    return target.storeName;
  }
  return extractName(deliveryTargetName, target);
}

function parseTarget(
  option: SelectOptions,
  unit: DeliveryTargetUnit
): ParsedDeliveryTarget | undefined {
  const value = option.value.toString();
  const parsed = parseValue(value, unit);
  if (!parsed) {
    return undefined;
  }
  return {
    ...parsed,
    storeName: extractName(option.label, parsed),
    value
  };
}

/**
 * 選項樹查不到時的備援判準。縣市底下的門市全部停用時後端連縣市節點都不回，活動存的
 * 那筆選取就只剩「臺北市」這個字串，而後端在這裡給的其他欄位都幫不上忙 —— label 與
 * value 相同、isGroup 是 false、逐間狀態也是空的（POS-8118 以實際活動確認過）。
 *
 * 只剩字串的形狀可用：門市／機台的 value 一定含斜線；CSV 匯入比對不到而存下的原文值
 * 是「縣市_店號_店名」那種 label，一定含底線（見 importLimitationsFromCsvData）。
 * 縣市名兩者都沒有。
 *
 * ⚠️ 這是**猜的**，不是判準：原文值若剛好不含底線會被誤判成一個空的縣市列。要拿掉
 * 這層猜測，得由後端在 limitation 上標出哪幾筆選的是縣市節點。
 */
function looksLikeMunicipality(value: string): boolean {
  return !value.includes('/') && !value.includes('_');
}

/**
 * 把選取的門市／機台依縣市分成兩層。
 *
 * `parentOptions` 是縣市層節點 value 對到它底下的門市（機台）。命中代表使用者
 * 選的是「整個縣市」，語意上等於把底下每一間都選起來，所以展開、筆數、門市列
 * 一律比照逐間選取，不另外寫成一列簡寫。
 */
export function groupDeliveryTargets(
  options: SelectOptions[],
  parentOptions: Map<string, SelectOptions[]>,
  unit: DeliveryTargetUnit
): DeliveryTargetGroup[] {
  const byMunicipality = new Map<string, DeliveryTargetGroup>();

  const groupOf = (key: string, label: string): DeliveryTargetGroup => {
    const existing = byMunicipality.get(key);
    if (existing) {
      return existing;
    }
    const created: DeliveryTargetGroup = {
      key: `city:${key}`,
      label,
      count: 0,
      targets: []
    };
    byMunicipality.set(key, created);
    return created;
  };

  const collect = (option: SelectOptions) => {
    const target = parseTarget(option, unit);
    if (!target) {
      // 縣市可能已經從選項裡消失（該縣市已無上架版位，或後端把「台」改成「臺」），
      // 此時整個縣市的選取與單一門市在資料上無法區分，一律歸入未知縣市。
      const group = groupOf(
        UNKNOWN_MUNICIPALITY_KEY,
        i18n.t('limitation.labels.unknownMunicipality')
      );
      group.targets.push({
        municipality: '',
        storeNum: '',
        storeName: option.label,
        value: option.value.toString()
      });
      return;
    }

    groupOf(target.municipality, target.municipality).targets.push(target);
  };

  options.forEach((option) => {
    const value = option.value.toString();
    const children = parentOptions.get(value);
    if (children && children.length > 0) {
      children.forEach(collect);
      return;
    }
    // 整個縣市選進來卻一間門市都列不出來（底下全部停用）：自成一列、展開是空的。
    // 歸進未知縣市會連選的是哪一個縣市都看不出來，而這一列要讓人看見的正是它空了。
    if (children || looksLikeMunicipality(value)) {
      groupOf(value, value);
      return;
    }
    collect(option);
  });

  byMunicipality.forEach((group) => {
    group.count = group.targets.length;
  });

  const named = Array.from(byMunicipality.entries())
    .filter(([key]) => key !== UNKNOWN_MUNICIPALITY_KEY)
    .map(([, group]) => group)
    .sort((a, b) => a.label.localeCompare(b.label));
  const unknown = byMunicipality.get(UNKNOWN_MUNICIPALITY_KEY);

  return [...named, ...(unknown ? [unknown] : [])];
}

/**
 * 這一組裡「整個縣市選進來」的那些縣市。
 *
 * 選父節點時活動只存得下縣市那一個值，底下有哪些門市是靠選項樹回查的 ——
 * 而那份樹只有上架中的版位，所以停用的門市會缺。逐間點選的清單從活動自己存的值
 * 來，不可能有缺，所以只有這些縣市需要補。
 */
export function parentSelectedMunicipalities(
  options: SelectOptions[],
  parentOptions: Map<string, SelectOptions[]>
): Set<string> {
  const selected = new Set<string>();
  options.forEach((option) => {
    const value = option.value.toString();
    // 底下一間都查不到也算選了：那正是整個縣市都停用的情形。選項樹連縣市節點都
    // 沒有時（該縣市已無任何上架版位）只剩字串的形狀判得出來。
    if (parentOptions.has(value) || looksLikeMunicipality(value)) {
      selected.add(value);
    }
  });
  return selected;
}

/** 縣市依 label 排序，未知縣市固定殿後。 */
function sortGroups(groups: DeliveryTargetGroup[]): DeliveryTargetGroup[] {
  const unknownKey = `city:${UNKNOWN_MUNICIPALITY_KEY}`;
  const named = groups
    .filter((group) => group.key !== unknownKey)
    .sort((a, b) => a.label.localeCompare(b.label));
  const unknown = groups.find((group) => group.key === unknownKey);
  return [...named, ...(unknown ? [unknown] : [])];
}

/**
 * 把「後端回了狀態、但清單上沒有」的門市補進來。
 *
 * 門市的版位全部停用之後就從選項樹消失，而選整個縣市時清單是靠選項樹展開的 ——
 * 於是**最該被看見的那一類門市（已停用＝門市異動）反而一間都列不出來**，
 * 統計卡卻照樣算得出數字，展開後找不到人。逐間點選的活動沒有這個洞，因為那份
 * 清單是從活動自己存的值來的。
 *
 * 狀態本身帶得動 value 與名字，足以自己撐起一列，所以缺的就用它補。
 */
export function withStatusOnlyTargets(
  groups: DeliveryTargetGroup[],
  statuses: DeliveryTargetStatus[],
  unit: DeliveryTargetUnit,
  parentMunicipalities: Set<string>
): DeliveryTargetGroup[] {
  const listed = new Set(
    groups.flatMap((group) => group.targets.map((target) => target.value))
  );
  // 狀態是一整份扁平清單，不說哪一間屬於哪一組限制。一間門市歸誰，由「誰把它所在
  // 的縣市整個選進來」決定 —— 拿「誰沒列到它」判斷會把別組的門市吸過來。
  const missing = statuses.filter((status) => {
    if (listed.has(status.deliveryTargetValue)) {
      return false;
    }
    const parsed = parseValue(status.deliveryTargetValue, unit);
    return !!parsed && parentMunicipalities.has(parsed.municipality);
  });
  if (missing.length === 0) {
    return groups;
  }

  // 不改動傳進來的那份：它是 memo 過的渲染輸入，原地改會讓畫面漏更新。
  const byKey = new Map(
    groups.map((group) => [
      group.key,
      { ...group, targets: [...group.targets] }
    ])
  );
  const groupOf = (key: string, label: string) => {
    const existing = byKey.get(`city:${key}`);
    if (existing) {
      return existing;
    }
    const created: DeliveryTargetGroup = {
      key: `city:${key}`,
      label,
      count: 0,
      targets: []
    };
    byKey.set(created.key, created);
    return created;
  };

  missing.forEach((status) => {
    const value = status.deliveryTargetValue;
    // 上面的篩選已經保證解得出來。
    const parsed = parseValue(value, unit)!;
    groupOf(parsed.municipality, parsed.municipality).targets.push({
      ...parsed,
      storeName: status.deliveryTargetName
        ? extractName(status.deliveryTargetName, parsed)
        : value,
      value
    });
  });

  const merged = Array.from(byKey.values()).map((group) => ({
    ...group,
    count: group.targets.length
  }));
  return sortGroups(merged);
}
